/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, { useContext, useEffect, useRef, useState } from 'react';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { debounce, cloneDeep, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { blockNumericKeyInput, blockNumericPaste, fplsFetch } from '../../../../shared/utils';
import { assignFormDesignToCSSVars, whenSideBySide } from '../fields-customise.style';
import { checkAddresAutoCompletionQuota } from '../../../builder/slice-actions/form';
import { ControlType, ctrlDefaults, elements } from '../../../../shared/form-lib';
import { ControlPropType, formDesignProps } from '../../ctrl_proptype';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import useClickAway from '../../../../hooks/click-away';
import { selectCtrlIdLabelMap } from '../../selectors';
import { controlValidate } from '../../../form/utils';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Input,
  Field,
} from '../../common-field-styles';
import {
  AddressFieldsWrapper,
  DualFieldsWrapper,
  NoOptPlaceholder,
  OptionsList,
  GoogleIcon,
  DDBody,
  Option,
} from './address.styled';

const defaultAddress = elements[ControlType.ADDRESS];

const getAddressFieldsPlaceholders = (ctrl) => {
  const fieldsOrder = ctrl.props?.address_fields_order || defaultAddress.address_fields_order;
  const addressPlaceholders = {};

  for (const field of fieldsOrder) {
    const { placeholder } = ctrl.props[field] ?? defaultAddress[field];
    addressPlaceholders[field] = placeholder;
  }

  return addressPlaceholders;
};

const getBaseAddressValue = (ctrl) => {
  const fieldsOrder = ctrl.props?.address_fields_order || defaultAddress.address_fields_order;
  const addressValue = {};
  
  const oldAddressFieldToValueKeyMap = {
    street_address_line_1: 'addressLine1',
    street_address_line_2: 'addressLine2',
    address_country: 'country',
    postal_code: 'postalCode',
    city: 'city/province',
    state: 'state'
  };
  
  for (const field of fieldsOrder) {
    if (!ctrl.props[field].display) continue;
    
    const label = ctrl.props[field].label || defaultAddress[field].label;

    if (ctrl?.value) {
      // BACKWARD COMPATIBILITY: Replace old field value key with field label.
      const oldFieldValueKey = oldAddressFieldToValueKeyMap[field];
      const oldFieldValue = ctrl.value?.[oldFieldValueKey];
      const newFieldValue = ctrl.value?.[field]?.value;
      let fieldValue = oldFieldValue ?? newFieldValue;

      // The key `state` existed in the old address value object and will resolve the
      // new object field value structure `{ label, value }` for new submissions.
      if (typeof fieldValue === 'object') fieldValue = fieldValue.value;

      addressValue[field] = { label, value: fieldValue };
    } else {
      addressValue[field] = { label, value: '' };
    }
  }

  return addressValue;
};

const Address = ({ ctrl, formDesign }) => {
  const ddListRef = useRef(null);
  const dispatch = useDispatch();
  const formId = window.FORM_ID;

  const { remaining: autoCompletionQuotaRemaining = 0 } = useSelector((state) => state.fieldEdit.autocomplete);
  const fieldsOrder = ctrl.props?.address_fields_order ?? defaultAddress.address_fields_order;
  const isDefaultFieldsOrder = isEqual(defaultAddress.address_fields_order, fieldsOrder);
  const { inForm, inBuilder, currentPageNo } = useContext(FormContext);
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const fieldPlaceholders = getAddressFieldsPlaceholders(ctrl);
  const baseAddressValue = getBaseAddressValue(ctrl);

  const [prevAddressDetails, setPrevAddressDetails] = useState(baseAddressValue);
  const [addressDetails, setAddressDetails] = useState(baseAddressValue);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isClick, setIsClick] = useState(false);
  const [quota, setQuota] = useState({});

  const [ctrlValidationError, setCtrlValidationError] = useState(ctrl._validationError);
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const responseStore = form?.response_store ?? 'formplus';
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const canUseAutoComplete = (
    (autoCompletionQuotaRemaining > 0 || quota?.remaining > 0) &&
    window.PLAN.name !== 'free_subscription' &&
    ctrl.props.autocomplete
  );

  const {
    suggestions: { status, data },
    init: initUsePlaces,
    clearSuggestions,
    setValue,
    ready,
    value
  } = usePlacesAutocomplete({
    cacheKey: 'address-field-autocompletion',
    initOnMount: false,
    debounce: 300,
  });

  const saveUpdatedCtrl = useRef(
    debounce((newValue) => {
      dispatch(
        setUserInputOnCtrl({
          ctrlId: ctrl.internal_id,
          page: currentPageNo - 1,
          property: 'value',
          newValue,
        })
      );
    }, 200)
  );
  
  // Sets address field values to hovered description details
  const handleAddressHover = (props) => {
    const { clicked = false, hovered = false, secondaryText, description, mainText, addr, e } = props;
    
    if (hovered) setPrevAddressDetails({ ...addressDetails });

    const parts = secondaryText.split(',').map((part) => part.trim());
    const addrLength = addr.length;

    const addressLine2 = parts.slice(0, -3).join(', ');
    const city = parts[parts.length - 3] || '';
    const state = addrLength >= 2 ? addr[addrLength - 2].value : '';
    const country = parts[parts.length - 1] || '';

    const newAddressDetails = cloneDeep(addressDetails);
    newAddressDetails.street_address_line_2.value = addressLine2;
    newAddressDetails.street_address_line_1.value = mainText;
    newAddressDetails.address_country.value = country;
    newAddressDetails.state.value = state;
    newAddressDetails.city.value = city;
    setAddressDetails(newAddressDetails);

    if (clicked) {
      setIsDropdownOpen(false);
      setValue(description);
      clearSuggestions();
      bumpQuotaUsage();
      setIsClick(true);

      saveUpdatedCtrl.current({
        ...newAddressDetails
      });
    }
  };

  const bumpQuotaUsage = async () => {
    const maxRetries = 15;
    let attempts = 0;

    while (attempts < maxRetries) {
      try {
        await fplsFetch({
          url: `/api/v2/public/address_autocompletion_quota/${formId}`,
          method: 'PUT',
          data: {
            form_id: formId,
            increase_by: 1,
          },
        });

        setQuota((prevQuota) => ({
          ...prevQuota,
          remaining: prevQuota.remaining - 1,
          used: prevQuota.used + 1,
        }));

        break;
      } catch (error) {
        console.error(`Failed to update quota (attempt ${attempts + 1}):`, error);
        attempts++;

        if (attempts >= maxRetries) {
          console.error('Max retry attempts reached. Could not update quota.');
        } else {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    }
  };

  const handleInputValue = (e) => {
    const { name: field, value } = e.target;
    const label = ctrl.props[field].label;
    const fieldDetails = { label, value };
    
    setAddressDetails((prevState) => ({
      ...prevState,
      [field]: fieldDetails
    }));
  };

  const resetHoverToPrev = () => {
    if (isClick) return setIsClick(false);
    
    setAddressDetails({
      ...prevAddressDetails
    });
  };

  // Toggle DDBody (Dropdown body)
  const handleToggleDD = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (isDropdownOpen) ddListRef.current.focus();
  };

  const handleChange = (e) => {
    if (e?.type === 'change') handleInputValue(e);
    const { name: field, value } = e.target;
    const label = ctrl.props[field].label;
    const fieldDetails = { label, value };

    saveUpdatedCtrl.current({
      ...addressDetails,
      [field]: fieldDetails
    });
  };

  const handleBlur = () => {
    const updatedCtrl = cloneDeep(ctrl);
    updatedCtrl.value = { ...addressDetails };

    controlValidate({
      setCtrlValidationError,
      ctrl: updatedCtrl,
      userCustomTexts,
      responseStore,
    });
  };

  useEffect(() => {
    setCtrlValidationError(ctrl._validationError);
  }, [ctrl._validationError]);
  
  useEffect(() => {
    if (inForm) initUsePlaces();
  }, [inForm, initUsePlaces]);

  useEffect(() => {
    // Ensure this runs only when addressDetails isn't really equal to ctrl.value.
    if (inForm && !isEqual(addressDetails, ctrl.value)) {
      saveUpdatedCtrl.current(baseAddressValue);
      setAddressDetails(baseAddressValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inForm, ctrl.value]);

  useEffect(() => {
    const fetchAddressQuota = async () => {
      try {
        if (inForm) {
          const { limit, used, remaining } = await fplsFetch({
            url: `/api/v2/public/address_autocompletion_quota/${formId}`,
            method: 'GET',
          });
          setQuota({ limit, used, remaining });
        } else {
          dispatch(checkAddresAutoCompletionQuota());
        }
      } catch (error) {
        console.error('Error loading address autocompletion quota:', error);
      }
    };

    fetchAddressQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inForm]);

  useClickAway({
    refs: [ddListRef],
    callback: () => {
      setIsDropdownOpen(false);
      clearSuggestions();
    },
    isActive: isDropdownOpen,
  });

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''} id={`fpl-${ctrl.internal_id}`}>
          <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.required}>
            <label
              htmlFor={`fpl-${ctrl.internal_id}`}
              dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }}
            />
            <SpecialPropsIndicators ctrl={ctrl} />
          </LabelWrapper>

          <InputWrapper className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            {canUseAutoComplete && (
              <AddressFieldsWrapper>
                <LabelWrapper notInBuilder={!inBuilder}>
                  <label>Search Address</label>
                </LabelWrapper>

                <InputWrapper full>
                  <Input
                    placeholder="Enter your address to quick search"
                    onChange={(e) => setValue(e.target.value)}
                    aria-describedby="descriptive text"
                    disabled={!inForm && !ready}
                    notInBuilder={!inBuilder}
                    onClick={handleToggleDD}
                    value={value}
                    type="search"
                    searchInput
                  />
                </InputWrapper>

                {/* <GoogleIcon src="https://storage.googleapis.com/fplsblog/google_icon.svg" alt="google_icon" /> */}

                {isDropdownOpen && (
                  <DDBody ref={ddListRef} id="dropdown-list">
                    {data.length ? (
                      <OptionsList id="DDOptionsList" role="list">
                        {status === 'OK' &&
                          data.map(
                            ({
                              structured_formatting: { main_text, secondary_text },
                              description,
                              place_id,
                              terms,
                            }) => (
                              <Option
                                onMouseOver={(e) =>
                                  handleAddressHover({
                                    secondaryText: secondary_text,
                                    mainText: main_text,
                                    clicked: false,
                                    hovered: true,
                                    description,
                                    addr: terms,
                                    e,
                                  })
                                }
                                onClick={(e) =>
                                  handleAddressHover({
                                    secondaryText: secondary_text,
                                    mainText: main_text,
                                    clicked: true,
                                    description,
                                    addr: terms,
                                    e,
                                  })
                                }
                                onMouseLeave={resetHoverToPrev}
                                key={place_id}
                                tabIndex="0">
                                {description}
                              </Option>
                            )
                          )}
                        <GoogleIcon src="https://storage.googleapis.com/fplsblog/google_icon.svg" alt="google_icon" />
                      </OptionsList>
                    ) : (
                      <NoOptPlaceholder notInBuilder={!inBuilder}>
                        {!value ? 'Start typing to get suggestions' : 'No suggestion(s) found'}
                      </NoOptPlaceholder>
                    )}
                  </DDBody>
                )}
              </AddressFieldsWrapper>
            )}
            
            {!isDefaultFieldsOrder ? (
              fieldsOrder.map((field) => (
                ctrl.props[field].display && (
                  <AddressFieldsWrapper key={field}>
                    <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props[field].required}>
                      <label>{ctrl.props[field].label}</label>
                    </LabelWrapper>

                    <InputWrapper full>
                      <Input
                        onKeyDown={field === 'state' ? blockNumericKeyInput : undefined}
                        onKeyUp={field === 'state' ? blockNumericKeyInput : undefined}
                        onPaste={field === 'state' ? blockNumericPaste : undefined}
                        placeholder={fieldPlaceholders[field]}
                        aria-describedby="descriptive text"
                        value={addressDetails[field].value}
                        notInBuilder={!inBuilder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!inForm}
                        name={field}
                      />
                    </InputWrapper>
                  </AddressFieldsWrapper>
                )
              ))
            ) : (
              <>
                {ctrl.props.street_address_line_1.display && (
                  <AddressFieldsWrapper>
                    <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.street_address_line_1.required}>
                      <label>{ctrl.props.street_address_line_1.label}</label>
                    </LabelWrapper>

                    <InputWrapper full>
                      <Input
                        placeholder={fieldPlaceholders.street_address_line_1}
                        value={addressDetails.street_address_line_1.value}
                        aria-describedby="descriptive text"
                        name="street_address_line_1"
                        notInBuilder={!inBuilder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!inForm}
                      />
                    </InputWrapper>
                  </AddressFieldsWrapper>
                )}

                {ctrl.props.street_address_line_2.display && (
                  <AddressFieldsWrapper>
                    <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.street_address_line_2.required}>
                      <label>{ctrl.props.street_address_line_2.label}</label>
                    </LabelWrapper>

                    <InputWrapper full>
                      <Input
                        placeholder={fieldPlaceholders.street_address_line_2}
                        value={addressDetails.street_address_line_2.value}
                        aria-describedby="descriptive text"
                        name="street_address_line_2"
                        notInBuilder={!inBuilder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!inForm}
                      />
                    </InputWrapper>
                  </AddressFieldsWrapper>
                )}

                {ctrl.props.postal_code.display && (
                  <AddressFieldsWrapper>
                    <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.postal_code.required}>
                      <label>{ctrl.props.postal_code.label}</label>
                    </LabelWrapper>

                    <InputWrapper full>
                      <Input
                        placeholder={fieldPlaceholders.postal_code}
                        value={addressDetails.postal_code.value}
                        aria-describedby="descriptive text"
                        notInBuilder={!inBuilder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name="postal_code"
                        disabled={!inForm}
                      />
                    </InputWrapper>
                  </AddressFieldsWrapper>
                )}

                <DualFieldsWrapper>
                  {ctrl.props.city.display && (
                    <AddressFieldsWrapper>
                      <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.city.required}>
                        <label>{ctrl.props.city.label}</label>
                      </LabelWrapper>

                      <InputWrapper full>
                        <Input
                          placeholder={fieldPlaceholders.city}
                          aria-describedby="descriptive text"
                          value={addressDetails.city.value}
                          notInBuilder={!inBuilder}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!inForm}
                          name="city"
                        />
                      </InputWrapper>
                    </AddressFieldsWrapper>
                  )}

                  {ctrl.props.state.display && (
                    <AddressFieldsWrapper>
                      <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.state.required}>
                        <label>{ctrl.props.state.label}</label>
                      </LabelWrapper>

                      <InputWrapper full>
                        <Input
                          placeholder={fieldPlaceholders.state}
                          aria-describedby="descriptive text"
                          value={addressDetails.state.value}
                          onKeyDown={blockNumericKeyInput}
                          onKeyUp={blockNumericKeyInput}
                          onPaste={blockNumericPaste}
                          notInBuilder={!inBuilder}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!inForm}
                          name="state"
                        />
                      </InputWrapper>
                    </AddressFieldsWrapper>
                  )}
                </DualFieldsWrapper>

                {ctrl.props.address_country.display && (
                  <AddressFieldsWrapper>
                    <LabelWrapper notInBuilder={!inBuilder} required={ctrl.props.address_country.required}>
                      <label>{ctrl.props.address_country.label || 'Country'}</label>
                    </LabelWrapper>

                    <InputWrapper full>
                      <Input
                        placeholder={fieldPlaceholders.address_country}
                        value={addressDetails.address_country.value}
                        aria-describedby="descriptive text"
                        notInBuilder={!inBuilder}
                        onChange={handleChange}
                        name="address_country"
                        onBlur={handleBlur}
                        disabled={!inForm}
                      />
                    </InputWrapper>
                  </AddressFieldsWrapper>
                )}
              </>
            )}

            <SubtextWrapper id="descriptive text">{ctrl.props.subtext}</SubtextWrapper>
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

Address.defaultProps = {
  formDesign: null,
};

Address.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape(formDesignProps),
};

export default Address;
